import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@utils/cn';

const typographyVariants = cva('', {
  variants: {
    intent: {
      displayLarge:
        'font-anybody text-[2.5rem] font-extrabold leading-[2.563rem] tracking-[-1px] sm:text-[3.75rem] sm:leading-[3.875rem] md:text-[5rem] md:leading-[5.1875rem]',
      displayMedium:
        'font-anybody text-[2.25rem] font-extrabold leading-[2.3125rem] tracking-[-1px] sm:text-[3rem] sm:leading-[3.125rem] md:text-[3.5rem] md:leading-[3.625rem]',
      displaySmall:
        'font-anybody text-[1.5rem] font-extrabold leading-[1.625rem] tracking-[-1px] sm:text-[2rem] sm:leading-[2.0625rem] md:text-[2.25rem] md:leading-[2.3125rem]',
      displayXSmall:
        'font-anybody text-[1.375rem] font-extrabold leading-[1.4375rem] tracking-[-0.5px] sm:text-[1.5rem] sm:leading-[1.5625rem] md:text-[2rem] md:leading-[2.0625rem]',
      headlineLarge:
        'font-inter text-[1.5rem] font-semibold leading-[1.8125rem] tracking-[-0.5px] sm:text-[2rem] sm:leading-10 sm:tracking-[-1px] md:text-[2.25rem] md:leading-[2.75rem]',
      headlineMedium:
        'font-inter text-[1.25rem] font-semibold leading-[1.8125rem] tracking-[-0.5px] sm:text-[1.5rem]',
      headlineSmall:
        'font-inter text-[1rem] font-semibold leading-[1.1875rem] tracking-[-0.5px] sm:text-[1.25rem] sm:leading-6',
      bodyLarge:
        'font-inter text-[1.25rem] font-medium leading-6 tracking-[-0.5px] md:text-[1.5rem] md:leading-[1.8125rem] md:tracking-[-1px]',
      bodyMedium:
        'font-inter text-[1.125rem] font-medium leading-[1.375rem] tracking-[-0.5px]',
      bodySmall:
        'font-inter text-[1rem] font-medium leading-[1.1875rem] tracking-[-0.5px]',
      bodyXSmall:
        'font-inter text-[0.875rem] font-medium leading-[1.0625rem] tracking-[-0.25px]',
      labelXLarge:
        'font-anybody text-[1.25rem] font-bold leading-[1.3125rem] md:text-[1.5rem] md:leading-[1.5625rem]',
      labelLarge: 'font-anybody text-[1.125rem] font-bold leading-[1.1875rem]',
      labelMedium: 'font-anybody text-[1rem] font-bold leading-[1.0625rem]',
      labelSmall: 'font-inter text-[0.875rem] font-bold leading-[1.0625rem]',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
    link: {
      false: null,
      true: ['underline'],
    },
  },
  defaultVariants: {
    intent: 'displayLarge',
    align: 'left',
    link: false,
  },
});

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, intent, align, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';
    return (
      <Comp
        className={cn(typographyVariants({ intent, align, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Typography.displayName = 'Typography';

export default Typography;

export { Typography, typographyVariants };
